/**
 * The lesson's world (decision 0045, core spec 4.6): a small world made by hand in which a new
 * player learns the game by playing it, five steps and five moves. Its chasms are listed, so the
 * board is the same for everyone and every step works; the seed gives only the ground.
 */
import type { Coord } from "./coords.ts";
import { DEFAULT_WORLD, type WorldConfig } from "./world.ts";

/** The seed of the lesson's ground and dressing: a forest troll, a huldra and a giant rise. */
export const LESSON_SEED = 207;

/**
 * The world: 15 by 15 tiles, the cabin in the middle. Five chasms are the lesson's, the first
 * five; the rest are a fence from five tiles out, so that the first tap's ground ends raggedly.
 */
export const LESSON_WORLD: WorldConfig = {
  chasmThreshold: DEFAULT_WORLD.chasmThreshold,
  safeRadiusSq: DEFAULT_WORLD.safeRadiusSq,
  coordLimit: 8,
  chasms: [
    [-1, -2],
    [-2, -1],
    [2, -1],
    [0, 2],
    [-3, 0],
    [-7, -7],
    [-5, -7],
    [-3, -7],
    [6, -7],
    [-6, -6],
    [-4, -6],
    [-2, -6],
    [1, -6],
    [3, -6],
    [6, -6],
    [-6, -5],
    [-3, -5],
    [-1, -5],
    [0, -5],
    [1, -5],
    [2, -5],
    [5, -5],
    [-6, -4],
    [-5, -4],
    [6, -4],
    [-6, -3],
    [-7, 0],
    [5, 0],
    [6, 0],
    [5, 1],
    [6, 1],
    [7, 1],
    [6, 2],
    [-7, 3],
    [-6, 3],
    [5, 3],
    [-6, 4],
    [5, 4],
    [-4, 5],
    [-3, 5],
    [3, 5],
    [5, 5],
    [0, 6],
    [-1, 7],
  ],
};

/**
 * A step of the lesson: the tiles it asks the player to reveal and to plant banners on, in any
 * order; the tile a ring marks, if any; and the number whose nine tiles a frame shows, if any.
 * The last step asks nothing: the banners lift and the creatures under them rise.
 */
export interface LessonStep {
  readonly reveal: readonly Coord[];
  readonly banners: readonly Coord[];
  readonly ring: Coord | null;
  readonly frame: Coord | null;
}

export const LESSON_STEPS: readonly LessonStep[] = [
  // The cabin: a tile of mist that opens the ground.
  { reveal: [{ x: -2, y: 2 }], banners: [], ring: { x: -2, y: 2 }, frame: null },
  // A number: the 1 below the cabin has one tile left in the mist.
  { reveal: [], banners: [{ x: 0, y: 2 }], ring: { x: 0, y: 2 }, frame: { x: 0, y: 1 } },
  // Safe ground: the 1 beside it has its creature, so its last tile is safe.
  { reveal: [{ x: -2, y: 0 }], banners: [], ring: null, frame: { x: -1, y: 1 } },
  // On your own: the 2 that tile shows has two tiles left in the mist.
  {
    reveal: [],
    banners: [
      { x: -2, y: -1 },
      { x: -3, y: 0 },
    ],
    ring: null,
    frame: { x: -2, y: 0 },
  },
  // Under the banners.
  { reveal: [], banners: [], ring: null, frame: null },
];
